import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Alert,
  Checkbox,
  TablePagination,
  TableSortLabel,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Popover,
} from "@mui/material";
import {
  MoreVert,
  VisibilityOutlined,
  DeleteOutline,
  FilterList,
  Close,
} from "@mui/icons-material";
import { quickbooksService } from "../../../../api/quickbooks";
import QBSyncDetailsDialog from "../../../dialogs/quickbooks/qbSyncDetailsDialog";
import CustomDateRangeDialog from "../../../dialogs/CustomDateRangeDialog";

const STATUS_CHIP_STYLES = {
  completed: { backgroundColor: "#dcfce7", color: "#166534" },
  in_progress: { backgroundColor: "#dbeafe", color: "#1d4ed8" },
  pending: { backgroundColor: "#fef9c3", color: "#92400e" },
  failed: { backgroundColor: "#fee2e2", color: "#b91c1c" },
  default: { backgroundColor: "#e5e7eb", color: "#374151" },
};

const formatStatusLabel = (value) =>
  value
    ? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "—";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = String(hours % 12 || 12).padStart(2, "0");
  return `${month}/${day}/${year}, ${hour12}:${minutes}:${seconds} ${ampm}`;
};

const getShortJobId = (value) => {
  if (!value) return "—";
  const str = String(value);
  const prefix = str.split("-")[0] || str;
  return `${prefix}…`;
};

const extractCursor = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.searchParams.get("cursor");
  } catch (error) {
    return null;
  }
};

const getAccountsCount = (job) => {
  const details = job?.details || {};
  if (Array.isArray(details.customer_ids)) {
    return details.customer_ids.length;
  }
  if (details.customer_id) {
    return 1;
  }
  return 0;
};

const deriveAccountName = (job, nameCache = {}) => {
  const details = job?.details || {};
  const results = job?.results || {};
  const accountList = Array.isArray(job?.customer_accounts)
    ? job.customer_accounts.filter(Boolean)
    : [];
  if (accountList.length > 1) {
    return "Bulk Sync";
  }
  if (accountList.length === 1) {
    const accountId = String(accountList[0].id || "");
    return (
      accountList[0].name ||
      nameCache[accountId] ||
      results?.[accountId]?.customerDetails?.name ||
      `Account ${accountId.slice(0, 8)}`
    );
  }
  const getNameFromResults = (id) => {
    if (!id) return null;
    const key = String(id);
    return results?.[key]?.customerDetails?.name || nameCache[key] || null;
  };

  if (details.customer_name) {
    return details.customer_name;
  }
  if (details.account_name) {
    return details.account_name;
  }

  const ids = Array.isArray(details.customer_ids) ? details.customer_ids : [];
  if (ids.length > 1) {
    return "Bulk Sync";
  }
  if (ids.length === 1) {
    const name = getNameFromResults(ids[0]);
    return name || `Account ${String(ids[0]).slice(0, 8)}`;
  }

  if (details.customer_id) {
    const name = getNameFromResults(details.customer_id);
    return name || `Account ${String(details.customer_id).slice(0, 8)}`;
  }
  return "—";
};

const deriveInitiatedBy = (job) =>
  job?.user_full_name || job?.user_email || job?.user || "";

const SyncStatusChip = ({ status }) => {
  if (!status) {
    return <Typography variant="body2">—</Typography>;
  }
  const normalized = status.toLowerCase();
  const styles = STATUS_CHIP_STYLES[normalized] || STATUS_CHIP_STYLES.default;
  return (
    <Chip
      size="small"
      label={formatStatusLabel(status)}
      sx={{
        ...styles,
        fontWeight: 500,
        textTransform: "capitalize",
      }}
    />
  );
};

export default function SyncHistoryTab() {
  const [searchTerm, setSearchTerm] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizing, setResizing] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const rowsPerPageRef = useRef(rowsPerPage);
  useEffect(() => {
    rowsPerPageRef.current = rowsPerPage;
  }, [rowsPerPage]);
  const [filters, setFilters] = useState({
    accountName: "",
    status: "",
    initiatedBy: "",
    startedAfter: "",
    startedBefore: "",
    completedAfter: "",
    completedBefore: "",
  });
  const [filterAnchors, setFilterAnchors] = useState({
    account: null,
    status: null,
    started: null,
    completed: null,
    initiated: null,
  });
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  const openFilter = (key, event) => {
    const anchor = event?.currentTarget || null;
    setFilterAnchors((prev) => ({ ...prev, [key]: anchor }));
  };
  const closeFilter = (key) => {
    setFilterAnchors((prev) => ({ ...prev, [key]: null }));
  };
  const [pagination, setPagination] = useState({
    next: null,
    previous: null,
    count: null,
  });
  const [page, setPage] = useState(0);
  const [pageCursors, setPageCursors] = useState([null]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    field: "started_at",
    direction: "desc",
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuJob, setMenuJob] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const customerNameCacheRef = useRef({});
  // eslint-disable-next-line no-unused-vars
  const [cacheVersion, setCacheVersion] = useState(0); // Used in useMemo dependency array to trigger re-renders

  // State for Started At filter
  const [startedSortOption, setStartedSortOption] = useState("");
  const [startedDateRange, setStartedDateRange] = useState("");
  const [startedCustomStartDate, setStartedCustomStartDate] = useState("");
  const [startedCustomEndDate, setStartedCustomEndDate] = useState("");
  const [startedCustomDateDialogOpen, setStartedCustomDateDialogOpen] = useState(false);

  // State for Completed At filter
  const [completedSortOption, setCompletedSortOption] = useState("");
  const [completedDateRange, setCompletedDateRange] = useState("");
  const [completedCustomStartDate, setCompletedCustomStartDate] = useState("");
  const [completedCustomEndDate, setCompletedCustomEndDate] = useState("");
  const [completedCustomDateDialogOpen, setCompletedCustomDateDialogOpen] = useState(false);

  const fetchJobs = useCallback(
    async ({ cursor = null, pageOverride = 0, search = "" } = {}) => {
      setLoading(true);
      setError(null);

      const params = { page_size: rowsPerPageRef.current };
      if (cursor) {
        params.cursor = cursor;
      }
      const searchTrimmed = search ? search.trim() : "";
      if (searchTrimmed) {
        params.search = searchTrimmed;
      }

      const initiatedByTrimmed = (filtersRef.current.initiatedBy || "").trim();

      // Apply column filters
      if (filtersRef.current.status) {
        params.status = filtersRef.current.status;
      }
      if (filtersRef.current.accountName) {
        params.account_name = filtersRef.current.accountName;
      }
      if (initiatedByTrimmed) {
        params.initiated_by = initiatedByTrimmed;
      }
      if (filtersRef.current.startedAfter) {
        params.started_after = filtersRef.current.startedAfter;
      }
      if (filtersRef.current.startedBefore) {
        params.started_before = filtersRef.current.startedBefore;
      }
      if (filtersRef.current.completedAfter) {
        params.completed_after = filtersRef.current.completedAfter;
      }
      if (filtersRef.current.completedBefore) {
        params.completed_before = filtersRef.current.completedBefore;
      }

      // Apply column filters
      if (filtersRef.current.status) {
        params.status = filtersRef.current.status;
      }
      if (filtersRef.current.accountName) {
        params.account_name = filtersRef.current.accountName;
      }
      if (initiatedByTrimmed) {
        params.initiated_by = initiatedByTrimmed;
      }
      if (filtersRef.current.startedAfter) {
        params.started_after = filtersRef.current.startedAfter;
      }
      if (filtersRef.current.startedBefore) {
        params.started_before = filtersRef.current.startedBefore;
      }
      if (filtersRef.current.completedAfter) {
        params.completed_after = filtersRef.current.completedAfter;
      }
      if (filtersRef.current.completedBefore) {
        params.completed_before = filtersRef.current.completedBefore;
      }

      try {
        const response = await quickbooksService.getAllSyncJobs(params);

        let allJobs = (response.data.results || []).filter((job) => {
          const details = job?.details || {};
          if (
            Array.isArray(details.customer_ids) &&
            details.customer_ids.length > 0
          ) {
            return true;
          }
          if (details.customer_id) {
            return true;
          }
          return !details.parent_job_id;
        });

        // Additional client-side filtering for Initiated By to match display name/email shown in the table
        if (initiatedByTrimmed) {
          const needle = initiatedByTrimmed.toLowerCase();
          allJobs = allJobs.filter((job) => {
            const display = deriveInitiatedBy(job).toLowerCase();
            return display.includes(needle);
          });
        }

        setJobs(allJobs);
        setPagination({
          next: response.data.next,
          previous: response.data.previous,
          count:
            typeof response.data.count === "number"
              ? response.data.count
              : null,
        });
        setSelectedRows([]);
        setPage(pageOverride);
        setPageCursors((prev) => {
          const updated = [...prev];
          updated[pageOverride] = cursor || null;
          return updated.slice(0, pageOverride + 1);
        });

        // Fetch customer names for single-account jobs lacking a name
        const missingIds = [];
        allJobs.forEach((job) => {
          const accounts = Array.isArray(job?.customer_accounts)
            ? job.customer_accounts.filter(Boolean)
            : [];
          if (accounts.length === 1) {
            const account = accounts[0];
            const id = String(account?.id || "");
            const hasName = Boolean(account?.name);
            if (id && !hasName && !customerNameCacheRef.current[id]) {
              missingIds.push(id);
            }
            return;
          }

          const details = job?.details || {};
          const ids = Array.isArray(details.customer_ids)
            ? details.customer_ids
            : details.customer_id
            ? [details.customer_id]
            : [];
          if (ids.length === 1) {
            const id = String(ids[0]);
            const hasName =
              details.customer_name ||
              details.account_name ||
              job?.results?.[id]?.customerDetails?.name ||
              customerNameCacheRef.current[id];
            if (id && !hasName) {
              missingIds.push(id);
            }
          }
        });

        if (missingIds.length) {
          const responses = await Promise.allSettled(
            missingIds.map((id) => quickbooksService.getCustomerById(id))
          );
          const fetched = {};
          responses.forEach((res, idx) => {
            if (res.status === "fulfilled" && res.value?.data?.name) {
              const id = String(missingIds[idx]);
              fetched[id] = res.value.data.name;
            }
          });
          if (Object.keys(fetched).length) {
            customerNameCacheRef.current = { ...customerNameCacheRef.current, ...fetched };
            setCacheVersion(prev => prev + 1);
            
            // No extra filtering here; updated cache ensures display names render when table re-renders
          }
        }
      } catch (err) {
        console.error("[SyncHistoryTab] fetchJobs error:", err);
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.errors?.[0] ||
          "Failed to load sync history.";
        setError(message);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchValue) => {
      if (searchValue === searchTerm) {
        if (!searchValue || !searchValue.trim()) {
          setPage(0);
          setPageCursors([null]);
          setSelectedRows([]);
          fetchJobs({ cursor: null, pageOverride: 0, search: "" });
        }
        return;
      }
      setSearchTerm(searchValue);
      setPage(0);
      setPageCursors([null]);
      setSelectedRows([]);
    },
    [searchTerm, fetchJobs]
  );

  const applyFilters = useCallback(
    (nextFilters = null) => {
      if (nextFilters) {
        filtersRef.current = nextFilters;
        setFilters(nextFilters);
      }
      setPage(0);
      setPageCursors([null]);
      setSelectedRows([]);
      fetchJobs({ cursor: null, pageOverride: 0, search: searchTerm });
    },
    [fetchJobs, searchTerm]
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Handlers for Started At filter
  const handleStartedDateRangeSelection = (value) => {
    if (value === "custom") {
      setStartedCustomDateDialogOpen(true);
      return;
    }
    setStartedDateRange(value);
    setStartedCustomStartDate("");
    setStartedCustomEndDate("");
  };

  const handleStartedCustomDateRangeClose = () => {
    setStartedCustomDateDialogOpen(false);
    if (!startedCustomStartDate || !startedCustomEndDate) {
      setStartedDateRange("");
    }
  };

  const handleStartedCustomDateRangeApply = (startDate, endDate) => {
    setStartedCustomStartDate(startDate);
    setStartedCustomEndDate(endDate);
    setStartedDateRange("custom");
    setStartedCustomDateDialogOpen(false);
  };

  const handleStartedFilterApply = () => {
    // Apply sort if sort option is selected
    if (startedSortOption) {
      setSortConfig({ field: "started_at", direction: startedSortOption });
    }

    // Apply date filter if date range is selected
    let startedAfter = "";
    let startedBefore = "";
    
    if (startedDateRange === "custom" && startedCustomStartDate && startedCustomEndDate) {
      startedAfter = startedCustomStartDate;
      startedBefore = startedCustomEndDate;
    } else if (startedDateRange) {
      const now = new Date();
      let startDate = new Date();
      
      switch (startedDateRange) {
        case "24h":
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case "7d":
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "30d":
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          break;
      }
      if (startedDateRange !== "") {
        startedAfter = startDate.toISOString().split("T")[0];
      }
    }

    const updated = {
      ...filtersRef.current,
      startedAfter,
      startedBefore,
    };
    applyFilters(updated);
    closeFilter("started");
  };

  const handleStartedFilterClear = () => {
    setStartedSortOption("");
    setStartedDateRange("");
    setStartedCustomStartDate("");
    setStartedCustomEndDate("");
    setSortConfig((prev) => {
      if (prev.field === "started_at") {
        return { field: "started_at", direction: "desc" };
      }
      return prev;
    });
    const updated = {
      ...filtersRef.current,
      startedAfter: "",
      startedBefore: "",
    };
    applyFilters(updated);
    closeFilter("started");
  };

  // Handlers for Completed At filter
  const handleCompletedDateRangeSelection = (value) => {
    if (value === "custom") {
      setCompletedCustomDateDialogOpen(true);
      return;
    }
    setCompletedDateRange(value);
    setCompletedCustomStartDate("");
    setCompletedCustomEndDate("");
  };

  const handleCompletedCustomDateRangeClose = () => {
    setCompletedCustomDateDialogOpen(false);
    if (!completedCustomStartDate || !completedCustomEndDate) {
      setCompletedDateRange("");
    }
  };

  const handleCompletedCustomDateRangeApply = (startDate, endDate) => {
    setCompletedCustomStartDate(startDate);
    setCompletedCustomEndDate(endDate);
    setCompletedDateRange("custom");
    setCompletedCustomDateDialogOpen(false);
  };

  const handleCompletedFilterApply = () => {
    // Apply sort if sort option is selected
    if (completedSortOption) {
      setSortConfig({ field: "completed_at", direction: completedSortOption });
    }

    // Apply date filter if date range is selected
    let completedAfter = "";
    let completedBefore = "";
    
    if (completedDateRange === "custom" && completedCustomStartDate && completedCustomEndDate) {
      completedAfter = completedCustomStartDate;
      completedBefore = completedCustomEndDate;
    } else if (completedDateRange) {
      const now = new Date();
      let startDate = new Date();
      
      switch (completedDateRange) {
        case "24h":
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case "7d":
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "30d":
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          break;
      }
      if (completedDateRange !== "") {
        completedAfter = startDate.toISOString().split("T")[0];
      }
    }

    const updated = {
      ...filtersRef.current,
      completedAfter,
      completedBefore,
    };
    applyFilters(updated);
    closeFilter("completed");
  };

  const handleCompletedFilterClear = () => {
    setCompletedSortOption("");
    setCompletedDateRange("");
    setCompletedCustomStartDate("");
    setCompletedCustomEndDate("");
    setSortConfig((prev) => {
      if (prev.field === "completed_at") {
        return { field: "started_at", direction: "desc" };
      }
      return prev;
    });
    const updated = {
      ...filtersRef.current,
      completedAfter: "",
      completedBefore: "",
    };
    applyFilters(updated);
    closeFilter("completed");
  };

  // Initial load and when searchTerm changes
  useEffect(() => {
    const searchTrimmed = searchTerm ? searchTerm.trim() : "";
    
    // Reset pagination when search changes
    setPageCursors([null]);
    setPage(0);
    setSelectedRows([]);
    
    // Call fetchJobs - it's stable so we can safely call it
    fetchJobs({ cursor: null, pageOverride: 0, search: searchTrimmed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]); // Only depend on searchTerm - fetchJobs is stable

  const handleChangePage = (event, newPage) => {
    if (newPage === page || loading) return;
    if (newPage > page) {
      const cursor = extractCursor(pagination.next);
      if (!cursor) return;
      fetchJobs({ cursor, pageOverride: newPage, search: searchTerm });
    } else {
      const cursor = pageCursors[newPage] || null;
      fetchJobs({ cursor, pageOverride: newPage, search: searchTerm });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    rowsPerPageRef.current = value;
    setRowsPerPage(value);
    setPageCursors([null]);
    fetchJobs({ cursor: null, pageOverride: 0, search: searchTerm });
  };

  const handleSelectRow = (event, jobId) => {
    event.stopPropagation();
    setSelectedRows((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const sortedJobs = useMemo(() => {
    const list = [...jobs];
    const { field, direction } = sortConfig;
    const multiplier = direction === "asc" ? 1 : -1;

    const getSortValue = (job) => {
      switch (field) {
        case "job_id":
          return job.id || "";
        case "account_name":
          return deriveAccountName(job, customerNameCacheRef.current).toLowerCase();
        case "status":
          return job.status || "";
        case "started_at":
          return job.started_at || "";
        case "completed_at":
          return job.completed_at || "";
        case "user":
          return deriveInitiatedBy(job).toLowerCase();
        case "accounts":
          return getAccountsCount(job);
        default:
          return job.id || "";
      }
    };

    list.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);
      if (valueA < valueB) return -1 * multiplier;
      if (valueA > valueB) return 1 * multiplier;
      return 0;
    });

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, sortConfig, cacheVersion]); // cacheVersion triggers re-render when cache updates

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { field, direction: "asc" };
    });
  };

  const handleMouseDown = (e, columnField) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ field: columnField, startX: e.clientX });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizing) return;

      const delta = e.clientX - resizing.startX;
      setColumnWidths((prev) => ({
        ...prev,
        [resizing.field]: Math.max(80, (prev[resizing.field] || 150) + delta),
      }));
      setResizing({ ...resizing, startX: e.clientX });
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  const handleOpenMenu = (event, job) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuJob(job);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuJob(null);
  };

  const handleViewDetails = () => {
    if (menuJob) {
      setSelectedJobId(menuJob.id);
      setDetailsDialogOpen(true);
    }
    handleCloseMenu();
  };

  const handleDeleteJob = async () => {
    if (!menuJob) return;
    setDeletingJobId(menuJob.id);
    try {
      await quickbooksService.deleteSyncJob(menuJob.id);
      const cursor = pageCursors[page] || null;
      await fetchJobs({ cursor, pageOverride: page });
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "Failed to delete sync job. Please try again."
      );
    } finally {
      setDeletingJobId(null);
      handleCloseMenu();
    }
  };

  const renderSortableHeader = (label, field, filterKey = null) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <TableSortLabel
        active={sortConfig.field === field}
        direction={sortConfig.field === field ? sortConfig.direction : "asc"}
        onClick={() => handleSort(field)}
        sx={{
          fontWeight: 600,
          color: "#111827",
          "& .MuiTableSortLabel-icon": { color: "#6B7280" },
        }}
      >
        {label}
      </TableSortLabel>
      {filterKey && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openFilter(filterKey, e);
          }}
          sx={{ color: "#6B7280", "&:hover": { color: "#4b5563" } }}
        >
          <FilterList fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  const totalCount = useMemo(() => {
    if (typeof pagination.count === "number") {
      return pagination.count;
    }
    if (pagination.next) {
      return (page + 2) * rowsPerPage;
    }
    return page * rowsPerPage + sortedJobs.length;
  }, [pagination.count, pagination.next, page, rowsPerPage, sortedJobs.length]);

  const renderContent = () => (
    <Box sx={{ px: 0, pt: 0, pb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Column filter popovers */}
      <Popover
        open={Boolean(filterAnchors.account)}
        anchorEl={filterAnchors.account}
        onClose={() => closeFilter("account")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disablePortal={false}
        slotProps={{
          root: { sx: { zIndex: 30000 } },
          paper: {
            sx: {
              zIndex: 30001,
              p: 2,
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              boxShadow: "0px 12px 36px rgba(15, 23, 42, 0.18)",
              minWidth: 320,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filter By Account Name
            </Typography>
            <IconButton
              size="small"
              onClick={() => closeFilter("account")}
              sx={{ color: "#6B7280" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <TextField
            size="small"
            placeholder="Search account name"
            value={filters.accountName}
            onChange={(e) => handleFilterChange("accountName", e.target.value)}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const updated = { ...filtersRef.current, accountName: "" };
                applyFilters(updated);
                closeFilter("account");
              }}
              sx={{ color: "#627CFB", textTransform: "none", fontWeight: 500 }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                applyFilters();
                closeFilter("account");
              }}
              sx={{
                backgroundColor: "#627CFB",
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#4f46e5" },
              }}
            >
              Filter
            </Button>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={Boolean(filterAnchors.status)}
        anchorEl={filterAnchors.status}
        onClose={() => closeFilter("status")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disablePortal={false}
        slotProps={{
          root: { sx: { zIndex: 200000 } },
          paper: {
            sx: {
              zIndex: 999999,
              p: 2,
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              boxShadow: "0px 12px 36px rgba(15, 23, 42, 0.18)",
              minWidth: 260,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filter Status
            </Typography>
            <IconButton
              size="small"
              onClick={() => closeFilter("status")}
              sx={{ color: "#6B7280" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              onClick={() => handleFilterChange("status", filters.status === "completed" ? "" : "completed")}
            >
              <Checkbox
                size="small"
                checked={filters.status === "completed"}
                sx={{
                  color: "#627CFB",
                  "&.Mui-checked": { color: "#627CFB" },
                  "& .MuiSvgIcon-root": { borderColor: "#627CFB" },
                }}
              />
              <Typography variant="body2">Completed</Typography>
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              onClick={() => handleFilterChange("status", filters.status === "failed" ? "" : "failed")}
            >
              <Checkbox
                size="small"
                checked={filters.status === "failed"}
                sx={{
                  color: "#627CFB",
                  "&.Mui-checked": { color: "#627CFB" },
                  "& .MuiSvgIcon-root": { borderColor: "#627CFB" },
                }}
              />
              <Typography variant="body2">Failed</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const updated = { ...filtersRef.current, status: "" };
                applyFilters(updated);
                closeFilter("status");
              }}
              sx={{ color: "#627CFB", textTransform: "none", fontWeight: 500 }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const updated = { ...filtersRef.current, status: filters.status };
                applyFilters(updated);
                closeFilter("status");
              }}
              sx={{
                backgroundColor: "#627CFB",
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#4f46e5" },
              }}
            >
              Filter
            </Button>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={Boolean(filterAnchors.started)}
        anchorEl={filterAnchors.started}
        onClose={() => closeFilter("started")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disablePortal={false}
        slotProps={{
          root: { sx: { zIndex: 9999 } },
          paper: {
            sx: {
              zIndex: 10000,
              p: 2,
              borderRadius: "8px",
              border: "1px solid rgba(229, 231, 235, 0.8)",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              backgroundColor: "#ffffff",
              minWidth: 500,
            },
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 500 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", color: "#111827" }}>
              Filter Started Date
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => closeFilter("started")}
              sx={{ p: 0.5 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: "flex", gap: 3 }}>
            {/* Left Side - Sort */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: "bold",
                  color: (startedSortOption || sortConfig.field === "started_at") ? "#627CFB" : "#111827",
                  fontSize: "0.75rem",
                  mb: 1
                }}
              >
                Sort by
              </Typography>

              <FormControl fullWidth size="small" sx={{ zIndex: 99999 }}>
                <InputLabel>Select Sort Option</InputLabel>
                <Select
                  value={startedSortOption}
                  onChange={(e) => {
                    setStartedSortOption(e.target.value);
                  }}
                  label="Select Sort Option"
                  MenuProps={{ 
                    sx: { 
                      zIndex: 99999,
                      "& .MuiPaper-root": {
                        minWidth: "200px"
                      }
                    } 
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: (startedSortOption || sortConfig.field === "started_at") ? "2px solid #627CFB" : "2px solid #e5e7eb"
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    }
                  }}
                >
                  <MenuItem value="asc">Newest to Oldest</MenuItem>
                  <MenuItem value="desc">Oldest to Newest</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Right Side - Date Range */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: "bold",
                  color: (startedDateRange || startedCustomStartDate || startedCustomEndDate || filters.startedAfter || filters.startedBefore) ? "#627CFB" : "#111827",
                  fontSize: "0.75rem",
                  mb: 1
                }}
              >
                Date range
              </Typography>

              <FormControl fullWidth size="small" sx={{ zIndex: 99999 }}>
                <InputLabel>Select Date Range</InputLabel>
                <Select
                  value={startedDateRange}
                  onChange={(e) => {
                    handleStartedDateRangeSelection(e.target.value);
                  }}
                  label="Select Date Range"
                  MenuProps={{ 
                    sx: { 
                      zIndex: 99999,
                      "& .MuiPaper-root": {
                        minWidth: "200px"
                      }
                    } 
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: (startedDateRange || startedCustomStartDate || startedCustomEndDate || filters.startedAfter || filters.startedBefore) ? "2px solid #627CFB" : "2px solid #e5e7eb"
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    }
                  }}
                >
                  <MenuItem value="24h">Last 24 hours</MenuItem>
                  <MenuItem value="7d">Last 7 Days</MenuItem>
                  <MenuItem value="30d">Last 30 Days</MenuItem>
                  <Divider />
                  <MenuItem value="custom">Custom range</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button 
              onClick={() => closeFilter("started")}
              size="small"
              sx={{ 
                color: "#627CFB",
                textTransform: "none",
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            {(startedSortOption || startedDateRange || startedCustomStartDate || startedCustomEndDate || sortConfig.field === "started_at" || filters.startedAfter || filters.startedBefore) && (
              <Button 
                onClick={handleStartedFilterClear}
                size="small"
                sx={{ 
                  color: "#627CFB",
                  textTransform: "none",
                  fontWeight: 500
                }}
              >
                Clear
              </Button>
            )}
            <Button 
              onClick={handleStartedFilterApply}
              variant="contained"
              size="small"
              sx={{ 
                backgroundColor: "#627CFB",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#4f46e5"
                }
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={Boolean(filterAnchors.completed)}
        anchorEl={filterAnchors.completed}
        onClose={() => closeFilter("completed")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disablePortal={false}
        slotProps={{
          root: { sx: { zIndex: 9999 } },
          paper: {
            sx: {
              zIndex: 10000,
              p: 2,
              borderRadius: "8px",
              border: "1px solid rgba(229, 231, 235, 0.8)",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              backgroundColor: "#ffffff",
              minWidth: 500,
            },
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 500 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", color: "#111827" }}>
              Filter Completed Date
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => closeFilter("completed")}
              sx={{ p: 0.5 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: "flex", gap: 3 }}>
            {/* Left Side - Sort */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: "bold",
                  color: (completedSortOption || sortConfig.field === "completed_at") ? "#627CFB" : "#111827",
                  fontSize: "0.75rem",
                  mb: 1
                }}
              >
                Sort by
              </Typography>

              <FormControl fullWidth size="small" sx={{ zIndex: 99999 }}>
                <InputLabel>Select Sort Option</InputLabel>
                <Select
                  value={completedSortOption}
                  onChange={(e) => {
                    setCompletedSortOption(e.target.value);
                  }}
                  label="Select Sort Option"
                  MenuProps={{ 
                    sx: { 
                      zIndex: 99999,
                      "& .MuiPaper-root": {
                        minWidth: "200px"
                      }
                    } 
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: (completedSortOption || sortConfig.field === "completed_at") ? "2px solid #627CFB" : "2px solid #e5e7eb"
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    }
                  }}
                >
                  <MenuItem value="asc">Newest to Oldest</MenuItem>
                  <MenuItem value="desc">Oldest to Newest</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Right Side - Date Range */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: "bold",
                  color: (completedDateRange || completedCustomStartDate || completedCustomEndDate || filters.completedAfter || filters.completedBefore) ? "#627CFB" : "#111827",
                  fontSize: "0.75rem",
                  mb: 1
                }}
              >
                Date range
              </Typography>

              <FormControl fullWidth size="small" sx={{ zIndex: 99999 }}>
                <InputLabel>Select Date Range</InputLabel>
                <Select
                  value={completedDateRange}
                  onChange={(e) => {
                    handleCompletedDateRangeSelection(e.target.value);
                  }}
                  label="Select Date Range"
                  MenuProps={{ 
                    sx: { 
                      zIndex: 99999,
                      "& .MuiPaper-root": {
                        minWidth: "200px"
                      }
                    } 
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: (completedDateRange || completedCustomStartDate || completedCustomEndDate || filters.completedAfter || filters.completedBefore) ? "2px solid #627CFB" : "2px solid #e5e7eb"
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    }
                  }}
                >
                  <MenuItem value="24h">Last 24 hours</MenuItem>
                  <MenuItem value="7d">Last 7 Days</MenuItem>
                  <MenuItem value="30d">Last 30 Days</MenuItem>
                  <Divider />
                  <MenuItem value="custom">Custom range</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button 
              onClick={() => closeFilter("completed")}
              size="small"
              sx={{ 
                color: "#627CFB",
                textTransform: "none",
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            {(completedSortOption || completedDateRange || completedCustomStartDate || completedCustomEndDate || sortConfig.field === "completed_at" || filters.completedAfter || filters.completedBefore) && (
              <Button 
                onClick={handleCompletedFilterClear}
                size="small"
                sx={{ 
                  color: "#627CFB",
                  textTransform: "none",
                  fontWeight: 500
                }}
              >
                Clear
              </Button>
            )}
            <Button 
              onClick={handleCompletedFilterApply}
              variant="contained"
              size="small"
              sx={{ 
                backgroundColor: "#627CFB",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#4f46e5"
                }
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={Boolean(filterAnchors.initiated)}
        anchorEl={filterAnchors.initiated}
        onClose={() => closeFilter("initiated")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disablePortal={false}
        slotProps={{
          root: { sx: { zIndex: 30000 } },
          paper: {
            sx: {
              zIndex: 30001,
              p: 2,
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              boxShadow: "0px 12px 36px rgba(15, 23, 42, 0.18)",
              minWidth: 320,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filter By Initiator
            </Typography>
            <IconButton
              size="small"
              onClick={() => closeFilter("initiated")}
              sx={{ color: "#6B7280" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <TextField
            size="small"
            placeholder="Search initiator"
            value={filters.initiatedBy}
            onChange={(e) => handleFilterChange("initiatedBy", e.target.value)}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const updated = { ...filtersRef.current, initiatedBy: "" };
                applyFilters(updated);
                closeFilter("initiated");
              }}
              sx={{ color: "#627CFB", textTransform: "none", fontWeight: 500 }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const updated = { ...filtersRef.current, initiatedBy: filters.initiatedBy };
                applyFilters(updated);
                closeFilter("initiated");
              }}
              sx={{
                backgroundColor: "#627CFB",
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#4f46e5" },
              }}
            >
              Filter
            </Button>
          </Box>
        </Box>
      </Popover>

      <Paper
        sx={{
          border: "1px solid #E5E7EB",
          boxShadow: "none",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#EEF0FD" }}>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    width: columnWidths.job_id || 200,
                    minWidth: columnWidths.job_id || 200,
                    maxWidth: columnWidths.job_id || "none",
                    position: "relative",
                  }}
                >
                  {renderSortableHeader("Job ID", "job_id")}
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "job_id")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    width: columnWidths.account_name || 240,
                    minWidth: columnWidths.account_name || 240,
                    maxWidth: columnWidths.account_name || "none",
                    position: "relative",
                  }}
                >
                  {renderSortableHeader("Account Name", "account_name", "account")}
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "account_name")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    width: columnWidths.status || 120,
                    minWidth: columnWidths.status || 120,
                    maxWidth: columnWidths.status || "none",
                    position: "relative",
                  }}
                >
                  {renderSortableHeader("Status", "status", "status")}
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "status")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    width: columnWidths.started_at || 180,
                    minWidth: columnWidths.started_at || 180,
                    maxWidth: columnWidths.started_at || "none",
                    position: "relative",
                  }}
                >
                  {renderSortableHeader("Started At", "started_at", "started")}
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "started_at")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    width: columnWidths.completed_at || 180,
                    minWidth: columnWidths.completed_at || 180,
                    maxWidth: columnWidths.completed_at || "none",
                    position: "relative",
                  }}
                >
                  {renderSortableHeader("Completed At", "completed_at", "completed")}
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "completed_at")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    width: columnWidths.user || 150,
                    minWidth: columnWidths.user || 150,
                    maxWidth: columnWidths.user || "none",
                    position: "relative",
                  }}
                >
                  {renderSortableHeader("Initiated By", "user", "initiated")}
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "user")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    textAlign: "left",
                    width: columnWidths.accounts || 100,
                    minWidth: columnWidths.accounts || 100,
                    maxWidth: columnWidths.accounts || "none",
                    position: "relative",
                  }}
                >
                  {renderSortableHeader("Accounts", "accounts")}
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "accounts")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: 60, minWidth: 60, borderBottom: "1px solid #E5E7EB" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && sortedJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : sortedJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm.trim()
                        ? "No jobs match your search."
                        : "No sync jobs found."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedJobs.map((job) => {
                  const isSelected = selectedRows.includes(job.id);
                  return (
                    <TableRow
                      key={job.id}
                      hover
                      sx={{
                        backgroundColor: isSelected ? "#EEF2FF" : "inherit",
                        borderBottom: "1px solid #F3F4F6",
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: "12px 16px",
                          width: columnWidths.job_id || 200,
                          minWidth: columnWidths.job_id || 200,
                          maxWidth: columnWidths.job_id || "none",
                        }}
                      >
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          overflow: "hidden",
                        }}>
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={(event) => handleSelectRow(event, job.id)}
                            sx={{ padding: 0 }}
                          />
                          <Tooltip
                            title={job.id}
                            enterDelay={400}
                            arrow
                            slotProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "#111827",
                                  color: "#fff",
                                  zIndex: 200002,
                                },
                              },
                              popper: {
                                sx: {
                                  zIndex: 200002,
                                },
                              },
                            }}
                          >
                            <span style={{ display: "inline-block", cursor: "default" }}>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  maxWidth: (columnWidths.job_id || 200) - 50,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {(columnWidths.job_id || 200) < 180
                                  ? getShortJobId(job.id)
                                  : job.id}
                              </Typography>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "12px 16px",
                          fontWeight: 500,
                          width: columnWidths.account_name || 240,
                          minWidth: columnWidths.account_name || 240,
                          maxWidth: columnWidths.account_name || "none",
                        }}
                      >
                        {deriveAccountName(job, customerNameCacheRef.current)}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "12px 16px",
                          width: columnWidths.status || 120,
                          minWidth: columnWidths.status || 120,
                          maxWidth: columnWidths.status || "none",
                        }}
                      >
                        <SyncStatusChip status={job.status} />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "12px 16px",
                          width: columnWidths.started_at || 180,
                          minWidth: columnWidths.started_at || 180,
                          maxWidth: columnWidths.started_at || "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "12px",
                            backgroundColor: "#f3f4f6",
                            color: "#374151",
                            fontWeight: 500,
                            fontSize: "0.875rem"
                          }}
                        >
                          {formatDateTime(job.started_at)}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "12px 16px",
                          width: columnWidths.completed_at || 180,
                          minWidth: columnWidths.completed_at || 180,
                          maxWidth: columnWidths.completed_at || "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "12px",
                            backgroundColor: "#f3f4f6",
                            color: "#374151",
                            fontWeight: 500,
                            fontSize: "0.875rem"
                          }}
                        >
                          {formatDateTime(job.completed_at)}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "12px 16px",
                          width: columnWidths.user || 150,
                      minWidth: columnWidths.user || 150,
                      maxWidth: columnWidths.user || "none",
                    }}
                  >
                    {deriveInitiatedBy(job) || "—"}
                  </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          padding: "12px 16px",
                          textAlign: "left",
                          width: columnWidths.accounts || 100,
                          minWidth: columnWidths.accounts || 100,
                          maxWidth: columnWidths.accounts || "none",
                        }}
                      >
                        {getAccountsCount(job)}
                      </TableCell>
                      <TableCell align="right" sx={{ padding: "12px 8px", width: 60 }}>
                        <IconButton size="small" onClick={(event) => handleOpenMenu(event, job)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {Math.min(totalCount, page * rowsPerPage + sortedJobs.length)} of {totalCount} jobs
          </Typography>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Box>
      </Paper>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disablePortal
        MenuListProps={{ "aria-labelledby": "sync-history-menu" }}
        PaperProps={{
          sx: {
            zIndex: 13000,
            minWidth: 200,
            boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.2)",
            borderRadius: 2,
          },
        }}
      >
        <MenuItem onClick={handleViewDetails} sx={{ px: 3, py: 1.5 }}>
          <ListItemIcon sx={{ minWidth: "32px" }}>
            <VisibilityOutlined fontSize="small" sx={{ color: "#6b7280" }} />
          </ListItemIcon>
          <ListItemText
            primary="View Sync Details"
            primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
          />
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem
          onClick={handleDeleteJob}
          disabled={deletingJobId === menuJob?.id}
          sx={{ px: 3, py: 1.5 }}
        >
          <ListItemIcon sx={{ minWidth: "32px" }}>
            {deletingJobId === menuJob?.id ? (
              <CircularProgress size={16} />
            ) : (
              <DeleteOutline fontSize="small" sx={{ color: "#ef4444" }} />
            )}
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            primaryTypographyProps={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#ef4444",
            }}
          />
        </MenuItem>
      </Menu>

      <QBSyncDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        jobId={selectedJobId}
      />

      <CustomDateRangeDialog
        open={startedCustomDateDialogOpen}
        onClose={handleStartedCustomDateRangeClose}
        onApply={handleStartedCustomDateRangeApply}
        initialStartDate={startedCustomStartDate}
        initialEndDate={startedCustomEndDate}
      />

      <CustomDateRangeDialog
        open={completedCustomDateDialogOpen}
        onClose={handleCompletedCustomDateRangeClose}
        onApply={handleCompletedCustomDateRangeApply}
        initialStartDate={completedCustomStartDate}
        initialEndDate={completedCustomEndDate}
      />
    </Box>
  );

  return {
    searchTerm,
    handleSearchChange,
    loading,
    error,
    renderContent,
  };
}
